import React from 'react';
import { Line, SchematicShell } from './SchematicPrimitives';

/**
 * Stairs elevation views:
 *   - front (default): looking at the bottom riser face — horizontal
 *       riser/tread stripes stacked across the full width.
 *   - left/right (view prop): the iconic stepped side profile (treads +
 *       risers stepping from floor to top). LEFT ascends L→R; RIGHT
 *       mirrors so ascent is R→L.
 *
 * The front view always shows the riser stack regardless of dimensions —
 * a wall-elevation view of stairs IS the riser face. The stepped side
 * profile lives exclusively in the L/R strips.
 *
 * Variant fields read:
 *   - obj.subtype = 'side' explicit override (front-strip only) for the
 *     rare case where a caller wants the side profile in the front strip.
 *   - obj.numSteps / obj.stepCount / obj.treads — step count override.
 * Defaults to ~7" rise / ~10" tread derived from dimensions, falling
 * back to 12 for the front view.
 */

const DEFAULT_STEPS = 12;
const TYPICAL_RISE_M = 7 * 0.0254;
const TYPICAL_TREAD_M = 10 * 0.0254;

export function resolveStairsStepCount(obj = {}) {
  const fields = [obj?.numSteps, obj?.stepCount, obj?.treads, obj?.riserCount];
  for (const f of fields) {
    if (Number.isFinite(f) && f > 0)
      return Math.min(24, Math.max(2, Math.round(f)));
  }
  return DEFAULT_STEPS;
}

function resolveSideStepCount(obj, heightMeters, depthMeters) {
  const explicit = resolveStairsStepCount(obj);
  if (explicit !== DEFAULT_STEPS) return explicit;
  // Derive from dimensions: pick the larger of (height / 7") and
  // (depth / 10") so we don't render a profile that overshoots the
  // available rise or run. Clamp to a readable range.
  const fromHeight = heightMeters > 0 ? heightMeters / TYPICAL_RISE_M : 0;
  const fromDepth = depthMeters > 0 ? depthMeters / TYPICAL_TREAD_M : 0;
  const n = Math.round(Math.max(fromHeight, fromDepth));
  if (!Number.isFinite(n) || n < 2) return DEFAULT_STEPS;
  return Math.min(14, Math.max(8, n));
}

export function StairsSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  obj = {},
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    const n = resolveSideStepCount(obj, height, depth);
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        drawFrame={false}
      >
        <SawtoothProfile
          width={depth}
          height={height}
          n={n}
          mirror={view === 'right'}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </SchematicShell>
    );
  }
  const n = resolveStairsStepCount(obj);
  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {obj?.subtype === 'side' ? (
        <SideFace
          width={width}
          height={height}
          n={n}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ) : (
        <FrontFace
          width={width}
          height={height}
          n={n}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </SchematicShell>
  );
}
StairsSchematic.supportsSideViews = true;

function FrontFace({ width, height, n, strokeColor, strokeWidth }) {
  // Stacked riser/tread bands. Skip y=0 and y=height — frame already drawn.
  const step = height / n;
  return (
    <g>
      {Array.from({ length: n - 1 }, (_, i) => {
        const y = (i + 1) * step;
        return (
          <Line
            key={i}
            x1={0}
            y1={y}
            x2={width}
            y2={y}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
    </g>
  );
}

function SideFace({ width, height, n, strokeColor, strokeWidth }) {
  // Right triangle with hypotenuse from (0, height) to (width, 0) and the
  // tread/riser steps inside. SVG y grows downward, so (0, height) is
  // bottom-left and (width, 0) is top-right.
  const tread = width / n;
  const rise = height / n;
  const lines = [];
  // Hypotenuse drawn as steps: walk from bottom-left up by N treads,
  // alternating horizontal tread and vertical riser strokes.
  for (let i = 0; i < n; i++) {
    const x0 = i * tread;
    const y0 = height - i * rise;
    const x1 = (i + 1) * tread;
    const y1 = height - (i + 1) * rise;
    // tread (horizontal at top of this step)
    lines.push(
      <Line
        key={`t${i}`}
        x1={x0}
        y1={y1}
        x2={x1}
        y2={y1}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
    // riser (vertical at front edge of this step)
    lines.push(
      <Line
        key={`r${i}`}
        x1={x0}
        y1={y0}
        x2={x0}
        y2={y1}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return <g>{lines}</g>;
}

/**
 * Sawtooth side-profile for the dedicated left/right elevation strips.
 *
 * Canonical "left" orientation: steps ascend left-to-right toward the
 * back of the flight. The flight's bottom riser is at x = 0, y = height;
 * the top tread reaches x = width, y = 0. We draw the stepped
 * hypotenuse plus a closing baseline along the floor (so the silhouette
 * reads as a solid stair body, not just a wavy line).
 *
 * "right" mirrors via the standard reflection transform.
 */
function SawtoothProfile({
  width,
  height,
  n,
  mirror,
  strokeColor,
  strokeWidth,
}) {
  const tread = width / n;
  const rise = height / n;
  const lines = [];
  for (let i = 0; i < n; i++) {
    const x0 = i * tread;
    const y0 = height - i * rise;
    const x1 = (i + 1) * tread;
    const y1 = height - (i + 1) * rise;
    // riser (vertical at front edge of this step)
    lines.push(
      <Line
        key={`r${i}`}
        x1={x0}
        y1={y0}
        x2={x0}
        y2={y1}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
    // tread (horizontal across top of this step)
    lines.push(
      <Line
        key={`t${i}`}
        x1={x0}
        y1={y1}
        x2={x1}
        y2={y1}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return (
    <g transform={mirror ? `translate(${width}, 0) scale(-1, 1)` : undefined}>
      {lines}
    </g>
  );
}
